import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ClipboardCheck, History, LogIn, LogOut } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, Button, Card, CardTitle, Modal, Skeleton } from "./ui";
import { formatClockTime, formatDate, formatDateTime } from "../lib/format";
import { formatMinutes } from "../lib/people";
import { currentPosition } from "../lib/geolocation";
import type { Attendance } from "../api/types";
import {
  ATTENDANCE_QUERY_KEYS,
  localDateInTimezone,
  pickSelfAttendance,
  reportIsComplete,
  saveReportThenCheckOut,
  selfAttendanceQueryKey,
  validateDailyReport,
  type ReportFieldErrors,
  type SavedDailyReport,
} from "../lib/attendance";

export function SelfPunchCard({ reportOnly = false }: { reportOnly?: boolean } = {}) {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;
  const queryClient = useQueryClient();

  const today = useMemo(() => localDateInTimezone(timezone), [timezone]);
  const now = useNow();

  // A manager's attendance and roster responses are team-scoped, so the card
  // must first resolve the caller's own employee row and then ask the API for
  // that employee only. Never derive "my record" from results[0].
  const selfEmployee = useQuery({
    queryKey: ["self-employee"],
    queryFn: ({ signal }) => api.employeeMe(signal),
    retry: false,
  });
  const employeeId = selfEmployee.data?.id;

  // A forgotten check-out can be older than yesterday, so the card reads the
  // employee's latest rows instead of a fixed two-day window. The state picker
  // ignores older completed shifts and keeps only an open or unreported one.
  const attendance = useQuery({
    queryKey: selfAttendanceQueryKey(today, employeeId),
    queryFn: ({ signal }) =>
      api.attendancePage(
        {
          employee: employeeId,
          page_size: 25,
          ordering: "-work_date",
        },
        signal,
      ),
    enabled: Boolean(employeeId),
    placeholderData: (prev) => prev,
  });

  // What is on the roster for today. The API scope is not the same for all
  // roles (staff see their published shifts, managers their team's roster), so
  // the employee filter and the client-side filter below are both required.
  const shifts = useQuery({
    queryKey: ["my-shifts", today, employeeId],
    queryFn: ({ signal }) =>
      api.shiftsPage(
        { employee: employeeId, from: today, to: today, page_size: 50 },
        signal,
      ),
    enabled: !reportOnly && Boolean(employeeId),
  });
  const ownShifts = useMemo(
    () =>
      (shifts.data?.results ?? []).filter(
        (shift) => shift.employee === employeeId && shift.status === "published",
      ),
    [shifts.data, employeeId],
  );
  const workingShift = ownShifts.find((shift) => !shift.is_off);
  const offShift = ownShifts.find((shift) => shift.is_off);
  const noPublishedShift = shifts.isSuccess && !workingShift && !offShift;
  const rosteredOff = shifts.isSuccess && !workingShift && Boolean(offShift);

  const ownAttendanceRows = useMemo(
    () =>
      (attendance.data?.results ?? []).filter(
        (row) => row.employee === employeeId,
      ),
    [attendance.data, employeeId],
  );

  const state = useMemo(
    () => pickSelfAttendance(ownAttendanceRows, today),
    [ownAttendanceRows, today],
  );

  const [workDone, setWorkDone] = useState("");
  const [learning, setLearning] = useState("");
  const [fieldErrors, setFieldErrors] = useState<ReportFieldErrors>({});
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [savedReport, setSavedReport] = useState<SavedDailyReport | null>(null);

  const invalidate = () => {
    for (const key of ATTENDANCE_QUERY_KEYS) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const checkIn = useMutation({
    mutationFn: async () => api.punchIn((await currentPosition()) ?? {}),
    onSuccess: invalidate,
  });

  const completeCheckout = useMutation({
    mutationFn: (values: {
      work_date: string;
      work_done: string;
      learning: string;
      savedReport: SavedDailyReport | null;
    }) =>
      saveReportThenCheckOut({
        workDate: values.work_date,
        workDone: values.work_done,
        learning: values.learning,
        savedReport: values.savedReport,
        submitReport: api.submitAttendanceReport,
        punchOut: api.punchOut,
        onReportSaved: setSavedReport,
      }),
    onSuccess: () => {
      setCheckOutOpen(false);
      setSavedReport(null);
      setWorkDone("");
      setLearning("");
      setFieldErrors({});
      invalidate();
    },
    onError: invalidate,
  });

  // A shift can be checked out without its report (for example by a manager).
  // The employee can finish that report here, which is what unlocks the next
  // check-in; the report is never re-created as a second row.
  const completeReport = useMutation({
    mutationFn: (values: {
      work_date: string;
      work_done: string;
      learning: string;
    }) => api.submitAttendanceReport(values),
    onSuccess: () => {
      setReportOpen(false);
      setWorkDone("");
      setLearning("");
      setFieldErrors({});
      invalidate();
    },
    onError: invalidate,
  });

  const busy =
    checkIn.isPending || completeCheckout.isPending || completeReport.isPending;

  const serverRow = completeCheckout.data ?? checkIn.data;
  const inCurrentShift = (candidate?: Attendance | null) =>
    Boolean(
      candidate &&
        candidate.employee === employeeId &&
        (candidate.work_date === today ||
          candidate.work_date === state.row?.work_date),
    );
  const row = (inCurrentShift(serverRow) ? serverRow : null) ?? state.row;
  const step = workflowStep(row);
  const elapsed = openMinutes(row?.clock_in_at, now);

  useEffect(() => {
    if (checkOutOpen && step !== "checked_in") setCheckOutOpen(false);
  }, [checkOutOpen, step]);

  useEffect(() => {
    if (reportOpen && step !== "checked_out") setReportOpen(false);
  }, [reportOpen, step]);

  useEffect(() => {
    if (savedReport && row?.work_date !== savedReport.work_date) {
      setSavedReport(null);
    }
  }, [row?.work_date, savedReport]);

  const fieldErrorFrom = (
    error: unknown,
    field: "work_done" | "learning",
  ): string | undefined => {
    const local = fieldErrors[field];
    if (local) return local;
    if (error instanceof ApiError) {
      const messages = error.fields?.[field];
      if (messages && messages.length > 0) return messages[0] ?? undefined;
    }
    return undefined;
  };

  const reportFieldError = (field: "work_done" | "learning"): string | undefined =>
    fieldErrorFrom(completeCheckout.error, field);

  const reportOnlyFieldError = (
    field: "work_done" | "learning",
  ): string | undefined => fieldErrorFrom(completeReport.error, field);

  const openCheckOut = () => {
    const workDate = row?.work_date;
    if (!workDate) return;
    const fromServer = reportIsComplete(row)
      ? {
          work_date: workDate,
          work_done: (row?.work_done ?? "").trim(),
          learning: (row?.learning ?? "").trim(),
        }
      : null;
    const saved =
      savedReport?.work_date === workDate ? savedReport : fromServer;
    setSavedReport(saved);
    setWorkDone(saved?.work_done ?? row?.work_done ?? "");
    setLearning(saved?.learning ?? row?.learning ?? "");
    setFieldErrors({});
    completeCheckout.reset();
    setCheckOutOpen(true);
  };

  const closeCheckOut = () => {
    if (completeCheckout.isPending) return;
    setCheckOutOpen(false);
    setFieldErrors({});
    completeCheckout.reset();
  };

  const submitCheckout = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (completeCheckout.isPending) return;
    const workDate = row?.work_date ?? savedReport?.work_date;
    if (!workDate) return;

    const errors = validateDailyReport(workDone, learning);
    setFieldErrors(errors);
    if (errors.work_done || errors.learning) return;

    completeCheckout.mutate({
      work_date: workDate,
      work_done: workDone,
      learning,
      savedReport,
    });
  };

  const openReport = () => {
    if (!row?.work_date) return;
    setWorkDone(row.work_done ?? "");
    setLearning(row.learning ?? "");
    setFieldErrors({});
    completeReport.reset();
    setReportOpen(true);
  };

  const closeReport = () => {
    if (completeReport.isPending) return;
    setReportOpen(false);
    setFieldErrors({});
    completeReport.reset();
  };

  const submitReport = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (completeReport.isPending) return;
    const workDate = row?.work_date;
    if (!workDate) return;

    const errors = validateDailyReport(workDone, learning);
    setFieldErrors(errors);
    if (errors.work_done || errors.learning) return;

    completeReport.mutate({
      work_date: workDate,
      work_done: workDone,
      learning,
    });
  };

  if (reportOnly) {
    const submitted = reportIsComplete(row);
    return (
      <Card>
        <CardTitle>
          <span className="flex items-center gap-2">
            Daily Work Report
            <Badge tone={submitted ? "emerald" : "amber"}>
              {submitted ? "submitted" : "missing"}
            </Badge>
          </span>
        </CardTitle>
        {step === "checked_out" ? (
          <p className="mb-2 text-xs text-slate-500">
            Checked out{" "}
            <span className="font-medium text-slate-700">
              {formatDateTime(row?.clock_out_at, timezone ?? undefined)}
            </span>
            .
          </p>
        ) : submitted ? (
          <p className="mb-2 text-xs text-slate-500">Submitted with your check-out.</p>
        ) : null}
        {submitted ? (
          <ReportSummary workDone={row?.work_done} learning={row?.learning} />
        ) : step === "checked_out" ? (
          <p className="text-sm text-amber-700">
            No report yet. Submit it from the attendance card to unlock your
            next check-in.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            No report yet. Submit your Daily Work Report during check-out.
          </p>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <div className="mb-3 flex items-start justify-between gap-3">
          <CardTitle className="mb-0">
            <span className="flex items-center gap-2">
              Attendance
              {state.overnight ? <Badge tone="sky">overnight</Badge> : null}
            </span>
          </CardTitle>
          <div className="shrink-0 text-right">
            <span className="sr-only">Current time</span>
            <p className="text-lg font-semibold leading-none tabular-nums tracking-tight text-slate-900">
              {formatClockTime(now, timezone ?? undefined)}
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              {formatDate(today, timezone ?? undefined)}
            </p>
          </div>
        </div>

        {selfEmployee.isError ? (
          <p role="alert" className="text-sm text-rose-600">
            No employee record is linked to this login, so attendance is
            unavailable.
          </p>
        ) : selfEmployee.isPending || !employeeId || attendance.isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : step === "not_checked_in" ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">No check-in yet today.</p>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Badge tone="slate">not checked in</Badge>
              <span>Status: No check-in yet</span>
            </div>
            {noPublishedShift ? (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                No published shift is assigned for today. Check-in requires an
                assigned shift; ask your manager to assign and publish your
                roster.
              </p>
            ) : rosteredOff ? (
              <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Today is marked as an off day on the roster. Check-in is
                unavailable for an off day.
              </p>
            ) : null}
            {shifts.isError ? (
              <p role="alert" className="text-xs text-rose-600">
                Today&apos;s roster could not be loaded, so check-in is
                unavailable until it can be checked again.
              </p>
            ) : null}
            <Button
              variant="primary"
              onClick={() => checkIn.mutate()}
              disabled={
                busy ||
                shifts.isPending ||
                noPublishedShift ||
                rosteredOff ||
                shifts.isError
              }
            >
              <LogIn size={15} /> Check in
            </Button>
          </div>
        ) : step === "checked_out" ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              Checked in{" "}
              <span className="font-medium text-slate-900">
                {formatDateTime(row?.clock_in_at, timezone ?? undefined)}
              </span>{" "}
              &rarr; out{" "}
              <span className="font-medium text-slate-900">
                {formatDateTime(row?.clock_out_at, timezone ?? undefined)}
              </span>{" "}
              &middot;{" "}
              <span className="font-medium">{formatMinutes(row?.worked_minutes)}</span>
            </p>
            <div className="flex items-center gap-2">
              <Badge tone={reportIsComplete(row) ? "emerald" : "amber"}>
                {reportIsComplete(row) ? "completed" : "report missing"}
              </Badge>
              {row?.late_minutes ? (
                <span className="text-xs text-amber-600">{row.late_minutes}m late</span>
              ) : null}
            </div>
            {state.overnight ? (
              <p className="text-xs text-slate-500">
                Previous shift from work date{" "}
                <span className="font-medium text-slate-700">{row?.work_date}</span>.
              </p>
            ) : null}
            {reportIsComplete(row) ? (
              <p className="text-xs text-slate-500">
                Attendance completed for this shift.
              </p>
            ) : (
              <>
                <p className="text-xs text-amber-700">
                  Daily Work Report missing. Your next check-in stays locked until
                  it is submitted.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  onClick={openReport}
                  disabled={busy}
                  aria-haspopup="dialog"
                  aria-expanded={reportOpen}
                >
                  <ClipboardCheck size={15} /> Submit Daily Work Report
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              Checked in{" "}
              <span className="font-medium text-slate-900">
                {formatDateTime(row?.clock_in_at, timezone ?? undefined)}
              </span>
            </p>
            <p className="text-xs text-slate-500">{formatMinutes(elapsed)} so far</p>
            <div className="flex items-center gap-2">
              <Badge tone={row?.status === "present" ? "emerald" : "amber"}>
                {(row?.status ?? "present").replace(/_/g, " ")}
              </Badge>
              {reportIsComplete(row) ? <Badge tone="sky">report submitted</Badge> : null}
            </div>
            {state.overnight ? (
              <p className="text-xs text-slate-500">
                Open check-in from work date{" "}
                <span className="font-medium text-slate-700">{row?.work_date}</span>. Check out to close that day.
              </p>
            ) : null}
          </div>
        )}

        {step === "checked_in" ? (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <Button
              type="button"
              variant="primary"
              onClick={openCheckOut}
              disabled={busy}
              aria-haspopup="dialog"
              aria-expanded={checkOutOpen}
              className="w-full"
            >
              <LogOut size={15} /> Check out
            </Button>
          </div>
        ) : null}

        {checkIn.isSuccess ? (
          <p className="mt-2 text-sm text-emerald-700">Checked in. Have a good one.</p>
        ) : null}

        <div className="mt-3 border-t border-slate-100 pt-2.5">
          <Link
            to="/people/me?tab=attendance"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            <History size={13} /> My attendance history
          </Link>
        </div>
      </Card>

      {checkIn.error ? (
        <p role="alert" className="text-sm text-rose-600">
          {errorText(checkIn.error)}
        </p>
      ) : null}

      <CheckOutDialog
        open={checkOutOpen && step === "checked_in"}
        row={row}
        timezone={timezone ?? undefined}
        workedMinutes={elapsed}
        pending={completeCheckout.isPending}
        savedReport={Boolean(savedReport)}
        workDone={workDone}
        learning={learning}
        fieldErrors={{
          work_done: reportFieldError("work_done"),
          learning: reportFieldError("learning"),
        }}
        error={errorText(completeCheckout.error)}
        onChangeWorkDone={(value) => {
          setWorkDone(value);
          setFieldErrors((errors) => ({ ...errors, work_done: undefined }));
          if (!completeCheckout.isPending) completeCheckout.reset();
        }}
        onChangeLearning={(value) => {
          setLearning(value);
          setFieldErrors((errors) => ({ ...errors, learning: undefined }));
          if (!completeCheckout.isPending) completeCheckout.reset();
        }}
        onClose={closeCheckOut}
        onSubmit={submitCheckout}
      />

      <CheckOutDialog
        mode="report"
        open={reportOpen && step === "checked_out"}
        row={row}
        timezone={timezone ?? undefined}
        workedMinutes={elapsed}
        pending={completeReport.isPending}
        savedReport={false}
        workDone={workDone}
        learning={learning}
        fieldErrors={{
          work_done: reportOnlyFieldError("work_done"),
          learning: reportOnlyFieldError("learning"),
        }}
        error={errorText(completeReport.error)}
        onChangeWorkDone={(value) => {
          setWorkDone(value);
          setFieldErrors((errors) => ({ ...errors, work_done: undefined }));
          if (!completeReport.isPending) completeReport.reset();
        }}
        onChangeLearning={(value) => {
          setLearning(value);
          setFieldErrors((errors) => ({ ...errors, learning: undefined }));
          if (!completeReport.isPending) completeReport.reset();
        }}
        onClose={closeReport}
        onSubmit={submitReport}
      />
    </div>
  );
}

export function SelfPunchCards() {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-5">
        <SelfPunchCard />
      </div>
      <div className="space-y-5">
        <SelfPunchCard reportOnly />
      </div>
    </div>
  );
}

export function CheckOutDialog({
  open,
  mode = "checkout",
  row,
  timezone,
  workedMinutes,
  pending,
  savedReport,
  workDone,
  learning,
  fieldErrors,
  error,
  onChangeWorkDone,
  onChangeLearning,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode?: "checkout" | "report";
  row: Attendance | undefined;
  timezone?: string;
  workedMinutes: number | null;
  pending: boolean;
  savedReport: boolean;
  workDone: string;
  learning: string;
  fieldErrors: ReportFieldErrors;
  error: string | null;
  onChangeWorkDone: (value: string) => void;
  onChangeLearning: (value: string) => void;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  const reportingOnly = mode === "report";
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, pending, onClose]);

  if (!open) return null;

  return (
    <Modal title={reportingOnly ? "Daily Work Report" : "Check out"} onClose={onClose}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span>
            Checked in{" "}
            <span className="font-medium text-slate-900">
              {formatDateTime(row?.clock_in_at, timezone)}
            </span>
          </span>
          {reportingOnly ? (
            <span>
              out{" "}
              <span className="font-medium text-slate-900">
                {formatDateTime(row?.clock_out_at, timezone)}
              </span>
            </span>
          ) : (
            <span>{formatMinutes(workedMinutes)} so far</span>
          )}
        </div>

        <p className="text-sm text-slate-600">
          {reportingOnly
            ? "This shift is already checked out. Submit the report to unlock your next check-in."
            : "Submit your Daily Work Report to finish your workday."}
        </p>

        <label className="block text-xs font-medium text-slate-600">
          Work Done
          <textarea
            value={workDone}
            onChange={(event) => onChangeWorkDone(event.target.value)}
            rows={3}
            aria-required="true"
            placeholder="Describe the work completed today..."
            disabled={pending}
            className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
          {fieldErrors.work_done ? (
            <span className="mt-1 block text-xs text-rose-600">{fieldErrors.work_done}</span>
          ) : null}
        </label>

        <label className="block text-xs font-medium text-slate-600">
          Learning
          <textarea
            value={learning}
            onChange={(event) => onChangeLearning(event.target.value)}
            rows={2}
            aria-required="true"
            placeholder="What did you learn today?"
            disabled={pending}
            className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
          {fieldErrors.learning ? (
            <span className="mt-1 block text-xs text-rose-600">{fieldErrors.learning}</span>
          ) : null}
        </label>

        {error ? (
          <p role="alert" className="text-sm text-rose-600">
            {error}
          </p>
        ) : null}

        {savedReport && error ? (
          <p className="text-xs text-slate-500">
            Your Daily Work Report is saved. Retrying will only finish check-out.
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <Button type="button" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={pending}>
            <ClipboardCheck size={15} /> {pending ? "Saving..." : "Submit Daily Report"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function workflowStep(
  row: Attendance | undefined,
): "not_checked_in" | "checked_in" | "checked_out" {
  if (!row?.clock_in_at) return "not_checked_in";
  if (row.clock_out_at) return "checked_out";
  return "checked_in";
}

function ReportSummary({ workDone, learning }: { workDone?: string | null; learning?: string | null }) {
  return (
    <dl className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-2.5 text-sm">
      <div>
        <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Work Done</dt>
        <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
          {workDone?.trim() || "Daily report missing"}
        </dd>
      </div>
      <div>
        <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Learning</dt>
        <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
          {learning?.trim() || "Daily report missing"}
        </dd>
      </div>
    </dl>
  );
}

function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

function openMinutes(clockIn: string | null | undefined, now: Date): number | null {
  if (!clockIn) return null;
  const started = new Date(clockIn).getTime();
  if (Number.isNaN(started)) return null;
  return Math.max(0, Math.floor((now.getTime() - started) / 60_000));
}

function errorText(error: unknown): string | null {
  return error instanceof ApiError ? error.message : null;
}
